/**
 * Dollie Skin — Recomandări în coș ("Se completează perfect cu...")
 * Folosește API-ul nativ Shopify Product Recommendations (intent=related),
 * complet automat din datele magazinului. Fără aplicații, fără cost.
 *
 * Custom element <cart-recommendations> citește configul din propriile data-attributes.
 * Conținutul e randat în Shadow DOM ca să nu fie șters de morph-ul temei la update.
 *
 * Excludere inteligentă: dacă în coș există un produs, nu recomandă nici bundle-urile
 * care îl conțin (ex: Beauty Wand în coș => nu recomandă "Setul de Conturare" sau
 * "The Glow System", ambele conținând Beauty Wand). Maparea bundle->componente
 * se setează prin data-bundle-map (JSON) pe element.
 */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      this.recoUrl = this.dataset.recommendationsUrl || '/recommendations/products';
      this.heading = this.dataset.heading || 'Se completează perfect cu';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} lei';
      this.limit = parseInt(this.dataset.limit || '2', 10);

      // Mapă bundle -> [id-uri produse componente]
      this.bundleMap = {};
      try {
        if (this.dataset.bundleMap) this.bundleMap = JSON.parse(this.dataset.bundleMap);
      } catch (e) {
        this.bundleMap = {};
      }

      this.attachShadow({ mode: 'open' });
      this.shadowRoot.innerHTML =
        '<style>' + CartRecommendations.styles + '</style>' +
        '<div class="wrap" hidden><p class="label"></p><div class="list"></div></div>';

      this._wrap = this.shadowRoot.querySelector('.wrap');
      this._label = this.shadowRoot.querySelector('.label');
      this._list = this.shadowRoot.querySelector('.list');
      this._label.textContent = this.heading;
    }

    this._onCartUpdate = () => this._schedule();
    document.addEventListener('cart:update', this._onCartUpdate);
    this.render();
  }

  disconnectedCallback() {
    if (this._onCartUpdate) document.removeEventListener('cart:update', this._onCartUpdate);
  }

  _schedule() {
    clearTimeout(this._t);
    this._t = setTimeout(() => this.render(), 250);
  }

  /**
   * Întoarce setul de id-uri "ocupate" de un produs: el însuși + componentele lui
   * dacă e bundle. Astfel un bundle și componentele lui se exclud reciproc.
   */
  _expand(productId) {
    const pid = Number(productId);
    const comp = this.bundleMap[String(pid)];
    return comp && comp.length ? [pid].concat(comp.map(Number)) : [pid];
  }

  async render() {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      if (!cart.items || cart.items.length === 0) return this._hide();

      // Ancora = produsul cel mai scump din coș (cross-sell premium)
      const anchor = cart.items.reduce((a, b) => (b.price > a.price ? b : a), cart.items[0]);

      // Toate id-urile "ocupate" de coș (produse + componentele bundle-urilor din coș)
      const owned = new Set();
      cart.items.forEach((i) => this._expand(i.product_id).forEach((x) => owned.add(x)));

      const url = this.recoUrl + '.json?product_id=' + anchor.product_id + '&intent=related&limit=20';
      const data = await (await fetch(url, { headers: { Accept: 'application/json' } })).json();

      const products = (data.products || [])
        .filter((p) => {
          if (!p.available) return false;
          // Exclude dacă produsul (sau orice componentă a lui, dacă e bundle)
          // se suprapune cu ce e deja în coș.
          return !this._expand(p.id).some((x) => owned.has(x));
        })
        .slice(0, this.limit);

      if (products.length === 0) return this._hide();
      this._paint(products);
    } catch (e) {
      this._hide();
    }
  }

  _hide() {
    if (this._wrap) { this._wrap.hidden = true; this._list.innerHTML = ''; }
  }

  _paint(products) {
    this._list.innerHTML = products
      .map((p) => {
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
        const vid = variant ? variant.id : '';
        const img = p.featured_image ? this._sized(p.featured_image, 160) : '';
        return (
          '<div class="card">' +
            '<a class="thumb" href="' + p.url + '"' + (img ? ' style="background-image:url(\'' + img + '\')"' : '') + ' aria-label="' + this._esc(p.title) + '"></a>' +
            '<div class="info">' +
              '<a class="title" href="' + p.url + '">' + this._esc(p.title) + '</a>' +
              '<span class="price">' + this._money(p.price) + '</span>' +
            '</div>' +
            '<button class="add" type="button" data-vid="' + vid + '"' + (vid ? '' : ' disabled') + '>Adaugă</button>' +
          '</div>'
        );
      })
      .join('');

    this._wrap.hidden = false;
    this._list.querySelectorAll('button.add').forEach((btn) => {
      btn.addEventListener('click', (e) => this._add(e.currentTarget));
    });
  }

  async _add(btn) {
    const id = btn.dataset.vid;
    if (!id) return;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = '…';

    try {
      const ids = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const body = JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: ids.join(','),
        sections_url: window.location.pathname,
      });

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });
      const json = await res.json();

      if (json.status) { // eroare (ex: stoc epuizat)
        btn.textContent = original;
        btn.disabled = false;
        return;
      }

      // Notifică tema să re-randeze coșul (cart-items-component ascultă 'cart:update')
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = {
        resource: {},
        sourceId: 'cart-recommendations',
        data: { source: 'cart-recommendations', sections: json.sections },
      };
      document.dispatchEvent(ev);

      btn.textContent = '✓ Adăugat';
      // re-randarea coșului va reîmprospăta și recomandările (drop produsul adăugat)
    } catch (err) {
      btn.textContent = original;
      btn.disabled = false;
    }
  }

  _sized(src, w) {
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', w);
      return u.href;
    } catch (e) {
      return src;
    }
  }

  _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  _money(cents) {
    const value = (Number(cents) || 0) / 100;
    const wd = (num, precision, thousands, decimal) => {
      const parts = num.toFixed(precision).split('.');
      const main = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return main + (parts[1] ? decimal + parts[1] : '');
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) => {
      switch (name) {
        case 'amount': return wd(value, 2, ',', '.');
        case 'amount_no_decimals': return wd(value, 0, ',', '.');
        case 'amount_with_comma_separator': return wd(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return wd(value, 0, '.', ',');
        case 'amount_with_space_separator': return wd(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return wd(value, 0, ' ', '');
        case 'amount_with_apostrophe_separator': return wd(value, 2, "'", '.');
        default: return wd(value, 2, ',', '.');
      }
    });
  }
}

CartRecommendations.styles = [
  ':host{display:block}',
  '.wrap{padding:16px 20px 6px;border-top:1px solid var(--color-border,rgba(0,0,0,.09))}',
  '.label{margin:0 0 12px;font-size:11px;letter-spacing:.16em;text-transform:uppercase;opacity:.55;font-weight:500}',
  '.list{display:flex;flex-direction:column;gap:12px}',
  '.card{display:flex;align-items:center;gap:12px}',
  '.thumb{width:52px;height:52px;flex:0 0 52px;border-radius:4px;background:#f3f1ee no-repeat center/cover;display:block}',
  '.info{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px}',
  '.title{font-size:13px;line-height:1.3;color:inherit;text-decoration:none;display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}',
  '.title:hover{text-decoration:underline}',
  '.price{font-size:12px;opacity:.6}',
  '.add{flex:0 0 auto;border:1px solid currentColor;background:transparent;color:inherit;font-size:10px;letter-spacing:.12em;text-transform:uppercase;padding:7px 14px;border-radius:0;cursor:pointer;white-space:nowrap;transition:background .2s,color .2s}',
  '.add:hover{background:currentColor;color:var(--color-background,#fff)}',
  '.add:disabled{cursor:default}',
].join('');

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}